import { useAppDispatch } from '@/app/hooks';
import { setSelectedMenuItem } from '@/components/Menu/menuSlice';
import { setZoomToSavedItem } from '@/components/PQMap/pqMapSlice';
import { setSaveItem, setSaveMode } from '@/components/SavePage/saveSlice';
import { useAccount } from '@/hooks/useAccount';
import { deleteAccountItem, setAccountItemVisibility } from '@/lib/account-data';
import { db } from "@/lib/db";
import { groupSavedItems } from '@/lib/saved-items';
import { SHAPE_ICONS } from '@/lib/shape-icons';
import type { SavedItem, SaveItemFilterOptions, SaveItemSortDirection, SaveItemSortOptions, SavePageMode } from "@/types";
import { ActionIcon, Box, Button, Center, Divider, Grid, Group, Image, Modal, ScrollArea, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from '@mantine/notifications';
import { IconArrowBigDownFilled, IconArrowBigUpFilled, IconCamera, IconCircle, IconCircleCheck, IconLocationFilled, IconPencil, IconTrash } from "@tabler/icons-react";
import { useLiveQuery } from "dexie-react-hooks";
import { useMemo, useRef, useState } from "react";

const SORT_OPTIONS: { value: SaveItemSortOptions; label: string }[] = [
    { value: 'date', label: 'Date' },
    { value: 'alpha', label: 'Alphabetical' },
];
// Shown two per row, in this order.
const FILTER_OPTIONS: { value: SaveItemFilterOptions; label: string }[] = [
    { value: 'Area', label: 'Area' },
    { value: 'Line', label: 'Line' },
    { value: 'Parcel', label: 'Parcel' },
    { value: 'Images', label: 'Photos' },
    { value: 'Point', label: 'Point' },
    { value: 'None', label: 'None' },
];
const formatDate = new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });

const SavedItemList: React.FC = () => {
    const dispatch = useAppDispatch();
    const { userId } = useAccount();
    const deletingRef = useRef(false);
    const [deleting, setDeleting] = useState(false);

    const savedItems = useLiveQuery(() => db.savedItems.where('ownerId').equals(userId).toArray(), [userId]);
    const [sortDirection, setSortDirection] = useState<SaveItemSortDirection>('asc');
    const [sortBy, setSortBy] = useState<SaveItemSortOptions>('date');
    const [filter, setFilter] = useState<SaveItemFilterOptions>('None');
    const [sortByModalOpened, sortByModalHandlers] = useDisclosure(false);
    const [filterByModalOpened, filterByModalHandlers] = useDisclosure(false);
    const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false);
    const [deleteItem, setDeleteItem] = useState<SavedItem | null>(null);
    const groups = useMemo(() => groupSavedItems(savedItems ?? [], sortBy, sortDirection, filter), [savedItems, sortBy, sortDirection, filter]);

    const openItem = (item: SavedItem, mode: SavePageMode) => {
        dispatch(setSelectedMenuItem('closed'));
        dispatch(setSaveMode(mode));
        dispatch(setSaveItem({ ...item }));
    }
    const onDeleteClickHandler = (item: SavedItem) => {
        setDeleteItem(item);
        deleteModalHandlers.open();
    }
    const onDeleteConfirmHandler = async () => {
        if (deleteItem && !deletingRef.current) {
            deletingRef.current = true;
            setDeleting(true);
            try {
                await deleteAccountItem(userId, deleteItem.id);
                deleteModalHandlers.close();
                setDeleteItem(null);
            } catch {
                notifications.show({ color: 'red', message: 'This item could not be deleted. Please try again.' });
            } finally {
                deletingRef.current = false;
                setDeleting(false);
            }
        }
    }
    const onVisibilityClickHandler = async (item: SavedItem) => {
        try {
            await setAccountItemVisibility(userId, item.id, !item.visible);
        } catch {
            notifications.show({ color: 'red', message: 'Visibility could not be updated. Please try again.' });
        }
    }

    return (
        <>
            <Stack gap={3} mb={5} h='100%'>
                <Group align='center' justify='space-between'>
                    <Group align='center' >
                        <Button style={{ color: 'black' }} variant='outline' radius='lg' color='pq-gray' onClick={sortByModalHandlers.toggle}>Sort By:&nbsp;<Text c='pq-red'>{sortBy === 'alpha' ? ' Alpha' : ' Date'}</Text></Button>
                        <ActionIcon size='md' color='pq-dark-gray' radius='xl' onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')} aria-label={sortDirection === 'asc' ? 'Sort descending' : 'Sort ascending'}>
                            {sortDirection === 'asc' ? <IconArrowBigUpFilled /> : <IconArrowBigDownFilled />}
                        </ActionIcon>
                    </Group>
                    <Button style={{ color: 'black' }} variant='outline' radius='lg' color='pq-gray' onClick={filterByModalHandlers.toggle}>Filter By:&nbsp;<Text c='pq-red'>{filter}</Text></Button>
                </Group>
                <ScrollArea h='100%' pt={5} pb={60} type='never' >
                    {groups.map((group) => (
                        <Stack mb={20} key={group.tag} gap={3}>
                            <Text fw={600}>{group.tag}</Text>
                            <Divider color='pq-med-gray' size={2} />
                            <Stack gap={0}>
                                {group.items.map((item, index) => (
                                    <Box key={item.id}>
                                        <Grid mx={10} align='center' >
                                            <Grid.Col span={1}>
                                                <Center>
                                                    <ActionIcon onClick={() => onVisibilityClickHandler(item)} color='grey' size='md' variant='transparent' radius='xl' aria-label={item.visible ? `Hide ${item.name}` : `Show ${item.name}`}>
                                                        {item.visible ? <IconCircleCheck /> : <IconCircle />}
                                                    </ActionIcon>
                                                </Center>
                                            </Grid.Col>
                                            <Grid.Col onClick={() => openItem(item, 'view')} span={1}>
                                                <Center w={18}>
                                                    <Image fit='contain' src={SHAPE_ICONS[item.itemType]} />
                                                </Center>
                                            </Grid.Col>
                                            <Grid.Col onClick={() => openItem(item, 'view')} span={6}>
                                                <Stack gap={0}>
                                                    <Text>{item.name}</Text>
                                                    <Text c='gray'>{formatDate.format(new Date(item.createdAt))}</Text>
                                                </Stack>
                                            </Grid.Col>
                                            <Grid.Col onClick={() => openItem(item, 'view')} span={1}>
                                                {item.hasImages && <Center w={20}><IconCamera /></Center>}
                                            </Grid.Col>
                                            <Grid.Col span={1}>
                                                <Center>
                                                    <ActionIcon onClick={() => dispatch(setZoomToSavedItem({ ...item }))} size='md' color='pq-red' variant='transparent' radius='xl' aria-label={`Zoom to ${item.name}`}>
                                                        <IconLocationFilled />
                                                    </ActionIcon>
                                                </Center>
                                            </Grid.Col>
                                            <Grid.Col span={1}>
                                                <Center>
                                                    <ActionIcon onClick={() => openItem(item, 'edit')} size='md' color='pq-red' variant='transparent' radius='xl' aria-label={`Edit ${item.name}`}>
                                                        <IconPencil />
                                                    </ActionIcon>
                                                </Center>
                                            </Grid.Col>
                                            <Grid.Col span={1}>
                                                <Center>
                                                    <ActionIcon onClick={() => onDeleteClickHandler(item)} size='sm' color='pq-red' variant='transparent' radius='xl' aria-label={`Delete ${item.name}`}>
                                                        <IconTrash />
                                                    </ActionIcon>
                                                </Center>
                                            </Grid.Col>
                                        </Grid>
                                        {index !== group.items.length - 1 && <Divider color='pq-light-gray' size={2} />}
                                    </Box>
                                ))}
                            </Stack>
                            <Divider color='pq-med-gray' size={2} />
                        </Stack>
                    ))}
                </ScrollArea>
            </Stack>
            <Modal withCloseButton={false} radius={15} opened={sortByModalOpened} onClose={sortByModalHandlers.toggle} title='Sort By:' centered>
                <Group align='center' justify='space-around'>
                    {SORT_OPTIONS.map(({ value, label }) => (
                        <Button key={value} radius={10} style={{ width: 120 }} variant={sortBy === value ? 'filled' : 'outline'} color={sortBy === value ? 'pq-red' : 'black'} onClick={() => { setSortBy(value); sortByModalHandlers.close(); }}>{label}</Button>
                    ))}
                </Group>
            </Modal>
            <Modal withCloseButton={false} radius={15} opened={filterByModalOpened} onClose={filterByModalHandlers.toggle} title='Filter By:' centered>
                <Stack>
                    {[0, 2, 4].map((row) => (
                        <Group key={row} align='center' justify='space-around'>
                            {FILTER_OPTIONS.slice(row, row + 2).map(({ value, label }) => (
                                <Button key={value} radius={10} style={{ width: 100 }} variant={filter === value ? 'filled' : 'outline'} color={filter === value ? 'pq-red' : 'black'} onClick={() => { setFilter(value); filterByModalHandlers.close(); }}>{label}</Button>
                            ))}
                        </Group>
                    ))}
                </Stack>
            </Modal>
            <Modal withCloseButton={false} radius={15} opened={deleteModalOpened} onClose={() => { if (!deletingRef.current) deleteModalHandlers.close(); }} title='Delete Saved Item' centered>
                <Stack>
                    <Text>Are you sure you want to delete this saved item?</Text>
                    <Group align='center' justify='space-around'>
                        <Button disabled={deleting} radius={10} style={{ width: 120 }} variant='outline' color='black' onClick={deleteModalHandlers.close}>Cancel</Button>
                        <Button loading={deleting} radius={10} style={{ width: 120 }} variant='outline' color='black' onClick={onDeleteConfirmHandler}>Ok</Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}
export default SavedItemList;
